import { Injectable } from '@angular/core';
import * as FileSaver from 'file-saver';
import autoTable from 'jspdf-autotable';
import jsPDF from 'jspdf';
import * as xlsx from 'xlsx-js-style';
import * as moment from 'moment';
import { Observable, BehaviorSubject } from 'rxjs';
import { AuthService } from './auth.service';
import { UserModel } from '../models/user.model';
import { SearchIncidentParameter } from 'src/app/main/core/models/incidents-report.model';
import { TIMEZONES } from 'src/app/_shared/core/constants/stealth.constants';
import { BRANDING_MEDIA } from 'src/app/_shared/core/constants/branding.constants';

export enum ExportLookUp {
    CSV = 1,
    XLSX = 2,
    PDF = 3
}

export class DataSheet {
    sheetName: string
    data: Array<any>
    columns: Array<any>
}

export class DataOrder {
    csv: boolean
    excel: boolean
    pdf: boolean
}

export class ExportColumns {
    columnName: string;
    field: string;
    header: string;
}


@Injectable({
    providedIn: 'root'
})
export class ExportIncidentReportsService {
    exportFile$: Observable<ExportLookUp>;
    exportFileSubject: BehaviorSubject<ExportLookUp>;
    isExporting$: Observable<boolean>;
    isExportingSubject: BehaviorSubject<boolean>;

    isExportingPDF$: Observable<boolean>;
    isExportingPDFSubject: BehaviorSubject<boolean>;

    isExportingCSV$: Observable<boolean>;
    isExportingCSVSubject: BehaviorSubject<boolean>;

    isExportingExcel$: Observable<boolean>;
    isExportingExcelSubject: BehaviorSubject<boolean>;
    isIncidentsLoaded$ = new BehaviorSubject<boolean>(false);
    userName: string;
    constructor(
        private readonly authService: AuthService
    ) {
        this.exportFileSubject = new BehaviorSubject<ExportLookUp>(undefined);
        this.exportFile$ = this.exportFileSubject.asObservable();
        this.isExportingSubject = new BehaviorSubject<boolean>(false);
        this.isExporting$ = this.isExportingSubject.asObservable();

        this.isExportingCSVSubject = new BehaviorSubject<boolean>(false);
        this.isExportingCSV$ = this.isExportingCSVSubject.asObservable();

        this.isExportingPDFSubject = new BehaviorSubject<boolean>(false);
        this.isExportingPDF$ = this.isExportingPDFSubject.asObservable();

        this.isExportingExcelSubject = new BehaviorSubject<boolean>(false);
        this.isExportingExcel$ = this.isExportingExcelSubject.asObservable();

        this.authService.currentUser$.subscribe((user: UserModel) => {
            if (user)
                this.userName = user.name + ' ' + user.surname;
        });
    }

    exportObservable(type: ExportLookUp) {
        if (type == ExportLookUp.CSV) {
            this.isExportingCSVSubject.next(true);
        }
        else if (type == ExportLookUp.XLSX) {
            this.isExportingExcelSubject.next(true);
        }
        else if (type == ExportLookUp.PDF) {
            this.isExportingPDFSubject.next(true);
        }
        this.exportFileSubject.next(type);
    }

    export(exportType: number, filename: string, sheet: DataSheet, filter: SearchIncidentParameter) {
        if (exportType === ExportLookUp.CSV) {
            this.exportCSV(filename, sheet, filter);
        }
        else if (exportType === ExportLookUp.XLSX) {
            this.exportXLSX(filename, sheet, filter);
        }
        else if (exportType === ExportLookUp.PDF) {
            this.exportPDF(filename, sheet, filter);
        }
    }

    exportCSV(filename: string, sheet: DataSheet, filter: SearchIncidentParameter) {
        const table = [];

        this.processCSVSheetData(sheet, table);

        const worksheet = xlsx.utils.json_to_sheet(table);
        let csvOutput: string = xlsx.utils.sheet_to_csv(worksheet);
        let outPutDate = new Date();
        let finalcsvOutput = `Incidents\nUser:,${this.userName},\nDate:,${moment(outPutDate).format('MM/DD/YYYY hh:mm a')}\n\n`

        if (filter.startDate || filter.endDate || filter.search || filter.escalationTypes) {
            if (filter.startDate)
                finalcsvOutput += `From Date:,${moment(filter.startDate).format('MM/DD/YYYY')}\n`;
            if (filter.endDate)
                finalcsvOutput += `To Date:,${moment(filter.endDate).format('MM/DD/YYYY')}\n`;
            if (filter.search)
                finalcsvOutput += `Search Text:,${filter.search}\n`;
            if (filter.escalationTypes)
                finalcsvOutput += `Escalation Type(s):,"${filter.escalationTypes}"\n`;
            finalcsvOutput += `\n`;
        }

        finalcsvOutput = finalcsvOutput + csvOutput;
        this.saveAsCSVFile(finalcsvOutput, filename);
    }

    private processCSVSheetData(sheet: DataSheet, table: object[]) {
        for (const row of sheet.data) {
            const tempRow: object = {};

            for (const { header, field } of sheet.columns) {
                if (header.includes("Date")) {
                    tempRow[header] = row[field] ? this.transformDateWithTimezone(row[field], row['zonename__c']) : row[field];
                } else {
                    tempRow[header] = row[field]?.toString();
                }
            }

            table.push(tempRow);
        }
    }

    saveAsCSVFile(buffer: any, fileName: string): void {
        let currentDt = new Date();
        let CSV_TYPE = 'text/csv;charset=utf-8';
        let CSV_EXTENSION = '.csv';
        const data: Blob = new Blob([buffer], {
            type: CSV_TYPE
        });
        FileSaver.saveAs(data, `${fileName}_${currentDt.toISOString().split('T')[0]}_${currentDt.getTime()}${CSV_EXTENSION}`);
        this.isExportingCSVSubject.next(false);
    }

    exportXLSX(filename: string, sheet: DataSheet, filter: SearchIncidentParameter): void {
        const data = [];

        const table = this.processXLSXSheetData(sheet);
        data.push(table);

        let headings: string[] = [];
        for (const column of sheet.columns) {
            headings.push(column["header"]);
        }

        const wb = xlsx.utils.book_new();
        const ws: xlsx.WorkSheet = xlsx.utils.json_to_sheet([]);

        xlsx.utils.sheet_add_aoa(ws, [["Incidents"]]);
        xlsx.utils.sheet_add_aoa(ws, [["User:", this.userName]], { origin: 'A2' });
        let outPutDate = new Date();
        xlsx.utils.sheet_add_aoa(ws, [["Date:", moment(outPutDate).format('MM/DD/YYYY hh:mm a')]], { origin: 'A3' });

        let rowIndex = 5;
        if (filter.startDate || filter.endDate || filter.search || filter.escalationTypes) {

            if (filter.startDate) {
                xlsx.utils.sheet_add_aoa(ws, [["From Date:", moment(filter.startDate).format('MM/DD/YYYY')]], { origin: `A${rowIndex}` });
                rowIndex++;
            }

            if (filter.endDate) {
                xlsx.utils.sheet_add_aoa(ws, [["To Date:", moment(filter.endDate).format('MM/DD/YYYY')]], { origin: `A${rowIndex}` });
                rowIndex++;
            }

            if (filter.search) {
                xlsx.utils.sheet_add_aoa(ws, [["Search Text:", filter.search]], { origin: `A${rowIndex}` });
                rowIndex++;
            }

            if (filter.escalationTypes) {
                xlsx.utils.sheet_add_aoa(ws, [["Escalation Type(s):", filter.escalationTypes]], { origin: `A${rowIndex}` });
                rowIndex++;
            }

            rowIndex++;

        }

        xlsx.utils.sheet_add_json(ws, [headings], { origin: `A${rowIndex}`, skipHeader: true });
        let headerIndex = rowIndex;
        rowIndex++;
        xlsx.utils.sheet_add_json(ws, data[0], { origin: `A${rowIndex}`, skipHeader: true });
        const colNames = [`A${headerIndex}`, `B${headerIndex}`, `C${headerIndex}`, `D${headerIndex}`];
        for (const itm of colNames) {
            if (ws[itm]) {
                ws[itm].s = {
                    font: { bold: true }
                };
            }
        }

        xlsx.utils.book_append_sheet(wb, ws, sheet.sheetName);

        const excelBuffer: any = xlsx.write(wb, { bookType: 'xlsx', type: 'array' });
        this.saveAsExcelFile(excelBuffer, filename);
    }

    private processXLSXSheetData(sheet: DataSheet): object[] {
        const table = [];

        for (const row of sheet.data) {
            const tempRow: object = {};

            for (const { header, field } of sheet.columns) {
                if (header.includes("Date")) {
                    tempRow[header] = row[field] ? this.transformDateWithTimezone(row[field], row['zonename__c']) : row[field];
                } else {
                    tempRow[header] = row[field]?.toString();
                }
            }

            table.push(tempRow);
        }

        return table;
    }

    saveAsExcelFile(buffer: any, fileName: string): void {
        let currentDt = new Date();
        let EXCEL_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
        let EXCEL_EXTENSION = '.xlsx';
        const data: Blob = new Blob([buffer], {
            type: EXCEL_TYPE
        });
        FileSaver.saveAs(data, `${fileName}_${currentDt.toISOString().split('T')[0]}_${currentDt.getTime()}${EXCEL_EXTENSION}`);
        this.isExportingExcelSubject.next(false);
    }


    exportPDF(filename: string, sheet: DataSheet, filter: SearchIncidentParameter): void {
        const doc = new jsPDF();
        const currentDt = new Date();
        const pdfExtension = '.pdf';

        let { dateFilterString, searchString, typeFilterString, numberOfLines } = this.buildFilterStrings(filter);

        let splitTitle = filter.escalationTypes && filter.escalationTypes.length > 0 ? doc.splitTextToSize(typeFilterString, 290) : [];
        if (splitTitle.length > 0) {
            numberOfLines += splitTitle.length;
        }

        const exportColumns = sheet.columns.map(col => col.header);
        const pdfData = [];

        for (const row of sheet.data) {
            const tempRow = this.processPDFRowData(sheet.columns, row);
            pdfData.push(tempRow);
        }

        autoTable(doc, {
            head: [exportColumns],
            body: pdfData,
            showHead: 'everyPage',
            margin: { top: 30 + (numberOfLines * 4) },
        });

        for (let i = 1; i < doc.internal.pages.length; i++) {
            doc.setFontSize(16);
            doc.setTextColor("#808080");
            doc.text('Incidents', 15, 10);

            doc.setFontSize(10);
            doc.setTextColor("#808080");
            doc.text('User: ' + this.userName, 15, 20);

            doc.setFontSize(10);
            doc.setTextColor("#808080");

            let yPointer = 25;
            if (dateFilterString.length > 0) {
                doc.text(dateFilterString, 15, yPointer);
                yPointer += 4;
            }

            if (searchString.length > 0) {
                doc.text(searchString, 15, yPointer);
                yPointer += 4;
            }

            if (splitTitle.length > 0) {
                doc.text(splitTitle, 15, yPointer);
            }

            doc.setFontSize(10);
            doc.setTextColor("#808080");
            doc.text('Date: ' + moment(currentDt).format('MM/DD/YYYY hh:mm a'), doc.internal.pageSize.getWidth() - 60, 20);

            // Add the image to the top right header
            const logoImg = new Image();
            logoImg.src = BRANDING_MEDIA.COMPANY_DARK_IMAGE;
            const headerWidth = 40; // Adjust the width of the header image
            const headerHeight = 11; // Adjust the height of the header image
            const headerX = doc.internal.pageSize.getWidth() - headerWidth - 15; // Adjust the X position of the header image
            const headerY = 5; // Adjust the Y position of the header image

            doc.addImage(logoImg, 'PNG', headerX, headerY, headerWidth, headerHeight);


            doc.setPage(i);
            doc.setFontSize(10);
            doc.setTextColor("#808080");
            doc.text(`Page ${i} of ${doc.internal.pages.length - 1}`, doc.internal.pageSize.getWidth() - 40, doc.internal.pageSize.getHeight() - 10);
        }

        const formattedDate = currentDt.toISOString().split('T')[0];
        const timestamp = currentDt.getTime();
        doc.save(`${filename}_${formattedDate}_${timestamp}${pdfExtension}`);
        this.isExportingPDFSubject.next(false);
    }

    private buildFilterStrings(filter: SearchIncidentParameter): {
        dateFilterString: string,
        searchString: string,
        typeFilterString: string,
        numberOfLines: number
    } {
        let dateFilterString = '';
        let searchString = '';
        let typeFilterString = '';
        let numberOfLines = 0;
    
        if (filter.startDate) {
            dateFilterString += "From Date: " + moment(filter.startDate).format('MM/DD/YYYY') + "   ";
            numberOfLines = 1;
        }
    
        if (filter.endDate) {
            dateFilterString += "To Date: " + moment(filter.endDate).format('MM/DD/YYYY');
            numberOfLines = 1;
        }
    
        if (filter.search) {
            searchString = "Search Text: " + filter.search;
            numberOfLines += 1;
        }

        if (filter.escalationTypes && filter.escalationTypes.length > 0) {
            typeFilterString = "Escalation Type(s) : " + filter.escalationTypes;
        }

        return { dateFilterString, searchString, typeFilterString, numberOfLines };
    }

    private processPDFRowData(columns: any[], row: any): any[] {
        const processedRow = [];

        for (const { header, field } of columns) {
            if (header.includes("Date")) {
                processedRow.push(row[field] ? this.transformDateWithTimezone(row[field], row['zonename__c']) : row[field]);
            } else {
                processedRow.push(row[field]?.toString());
            }
        }
        return processedRow;
    }

    private transformDateWithTimezone(incidentDate: string, zoneName: string) {
        if (incidentDate && zoneName) {
            const timeZone = TIMEZONES[zoneName] || "UTC";
            return moment.tz(incidentDate, timeZone).format('MM/DD/YYYY');
        }
        return incidentDate;
    }

}